// Connect7 — Realtime Live Dashboard (Slice 15).
// Advanced, omnichannel ViciDial-style live floor for supervisors + admins.
// Shows every logged-in agent's CURRENT activity (voice call, WhatsApp,
// Instagram, SMS, Email, Web Chat, break, wrap, dead call, …) with a live
// status duration, plus Spy / Whisper / Barge for live voice calls and a
// read-only transcript view for live digital conversations.
//
// Refreshes via a 5s poll AND WebSocket events (agent.state, call.*,
// presence.changed, channel.inbound, monitor.*).
import { useEffect, useRef, useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Activity, Headphones, Users2, Phone, PhoneCall, PhoneOff, Pause,
  Coffee, Disc, Eye, MessageSquare, AlertCircle, Loader2, Search,
  MessageCircle, Instagram, Facebook, Mail, Globe, Send, Voicemail,
  RadioTower, Volume2, Skull, PhoneOutgoing, Target, ChevronDown,
  LogIn, CheckCircle2, UserCheck, WifiOff, PhoneIncoming, PhoneMissed,
  Gauge, TrendingUp, Layers, BarChart3, Hash, Signal, Users, RefreshCw,
  NotebookPen, Unplug, PhoneForwarded, Clock, Megaphone, LogOut,
} from "lucide-react";
import api from "@/lib/api";
import { DISPLAY_TZ } from "@/lib/app-utils";
import { useAuth } from "@/contexts/AuthContext";
import MonitorPhone from "@/components/MonitorPhone";
import { toast } from "sonner";

const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const WS_BASE = RAW_BACKEND.replace(/^http/, "ws");

function fmtAge(s) {
  s = Math.max(0, Math.floor(s || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), sec = s % 60;
  if (m < 60) return `${m}:${sec.toString().padStart(2, "0")}`;
  const h = Math.floor(m / 60);
  return `${h}h ${(m % 60).toString().padStart(2, "0")}m`;
}

// Channel visual metadata for digital activity.
const CHANNEL_META = {
  whatsapp:  { Icon: MessageCircle, dot: "bg-green-500",  chip: "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300" },
  sms:       { Icon: MessageSquare, dot: "bg-cyan-500",   chip: "bg-cyan-100 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300" },
  email:     { Icon: Mail,          dot: "bg-indigo-500", chip: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300" },
  webchat:   { Icon: Globe,         dot: "bg-teal-500",   chip: "bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300" },
  instagram: { Icon: Instagram,     dot: "bg-pink-500",   chip: "bg-pink-100 text-pink-700 dark:bg-pink-950/40 dark:text-pink-300" },
  facebook:  { Icon: Facebook,      dot: "bg-blue-600",   chip: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" },
  telegram:  { Icon: Send,          dot: "bg-sky-500",    chip: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300" },
  voicemail: { Icon: Voicemail,     dot: "bg-violet-500", chip: "bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300" },
};

// Compute color tier + icon for an agent's unified activity (ViciDial-style
// escalation: longer = hotter).
function activityVisual(ca) {
  const d = ca?.duration_seconds || 0;
  const s = ca?.status;
  if (ca?.kind === "voice") {
    if (s === "dead_call")
      return { Icon: Unplug, dot: "bg-zinc-900", chip: "bg-zinc-900 text-white border-zinc-700", ring: "border-zinc-700" };
    if (s === "ringing" || s === "originating")
      return { Icon: PhoneCall, dot: "bg-amber-500", chip: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300", ring: "border-amber-300 dark:border-amber-800" };
    // talking (in call) — BLUE family, escalating by duration. Deliberately NOT
    // green (that's Available/Ready) so an on-call agent is never confused with
    // an idle-ready one.
    if (d >= 300) return { Icon: PhoneCall, dot: "bg-indigo-700", chip: "bg-indigo-200 text-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-200", ring: "border-indigo-400 dark:border-indigo-800" };
    if (d >= 60)  return { Icon: PhoneCall, dot: "bg-blue-600", chip: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300", ring: "border-blue-300 dark:border-blue-900" };
    return { Icon: PhoneCall, dot: "bg-sky-500", chip: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300", ring: "border-sky-300 dark:border-sky-800" };
  }
  if (ca?.kind === "digital") {
    const m = CHANNEL_META[ca.channel] || { Icon: MessageSquare, dot: "bg-violet-500", chip: "bg-violet-100 text-violet-700" };
    return { Icon: m.Icon, dot: m.dot, chip: m.chip, ring: "border-violet-300 dark:border-violet-900" };
  }
  // idle
  if (s === "manual")
    return { Icon: PhoneOutgoing, dot: "bg-cyan-500", chip: "bg-cyan-100 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300", ring: "border-cyan-300 dark:border-cyan-900" };
  if (s === "paused" || s === "break" || s === "lunch") {
    if (d >= 300) return { Icon: Coffee, dot: "bg-rose-700", chip: "bg-rose-200 text-rose-900 dark:bg-rose-950/40 dark:text-rose-300", ring: "" };
    return { Icon: Coffee, dot: "bg-rose-500", chip: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", ring: "" };
  }
  if (s === "wrap")
    return { Icon: NotebookPen, dot: "bg-orange-500", chip: "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300", ring: "border-orange-300 dark:border-orange-900" };
  if (s === "ready" || s === "available") {
    // Always GREEN (ready), only deepening the shade the longer an agent idles.
    if (d >= 300) return { Icon: Activity, dot: "bg-emerald-700", chip: "bg-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300", ring: "" };
    return { Icon: Activity, dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300", ring: "" };
  }
  return { Icon: PhoneOff, dot: "bg-slate-400", chip: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400", ring: "" };
}

// Which filter bucket an agent belongs to (for the filter chips).
function bucketOf(ca) {
  if (ca?.kind === "voice") {
    if (ca.status === "dead_call") return "dead";
    if (ca.status === "ringing" || ca.status === "originating") return "ringing";
    return "oncall";
  }
  if (ca?.kind === "digital") return "digital";
  const s = ca?.status;
  if (s === "paused" || s === "break" || s === "lunch" || s === "manual") return "paused";
  if (s === "wrap") return "wrap";
  if (s === "ready" || s === "available") return "ready";
  return "offline";
}

const FILTERS = [
  { key: "all",     label: "All" },
  { key: "oncall",  label: "On Call" },
  { key: "ringing", label: "Ringing" },
  { key: "digital", label: "Digital" },
  { key: "ready",   label: "Ready" },
  { key: "paused",  label: "Paused" },
  { key: "wrap",    label: "Wrap" },
  { key: "dead",    label: "Dead Call" },
  { key: "offline", label: "Offline" },
];

export default function SupervisorFloor() {
  const { user } = useAuth();
  const [floor, setFloor] = useState(null);
  const [activeMonitors, setActiveMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [campaignIds, setCampaignIds] = useState([]); // [] = all campaigns
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [transcript, setTranscript] = useState(null); // {agent, convId, messages, loading}
  const wsRef = useRef(null);
  const fetchedAtRef = useRef(Date.now());
  const campaignIdsRef = useRef([]);
  useEffect(() => { campaignIdsRef.current = campaignIds; }, [campaignIds]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  async function fetchFloor(silent = false) {
    if (!silent) setRefreshing(true);
    try {
      const ids = campaignIdsRef.current;
      const qs = ids.length ? `?campaign_ids=${encodeURIComponent(ids.join(","))}` : "";
      const [f, m] = await Promise.all([
        api.get(`/supervisor/floor${qs}`),
        api.get("/supervisor/monitor/active"),
      ]);
      setFloor(f.data);
      fetchedAtRef.current = Date.now();
      setActiveMonitors(m.data || []);
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Couldn't load floor");
    } finally {
      setLoading(false);
      if (!silent) setRefreshing(false);
    }
  }

  useEffect(() => {
    fetchFloor();
    const t = setInterval(() => fetchFloor(true), 5000);
    return () => clearInterval(t);
  }, [campaignIds]);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("c7_token");
    if (!token) return;
    let stopped = false;
    let reconnect = null;
    function connect() {
      if (stopped) return;
      const ws = new WebSocket(`${WS_BASE}/api/ws/agent?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        let msg; try { msg = JSON.parse(e.data); } catch { return; }
        if (["call.new", "call.state", "call.ended", "presence.changed",
             "agent.state", "channel.inbound", "dialer.bridged", "dialer.ended",
             "monitor.started", "monitor.ended"].includes(msg.type)) {
          fetchFloor(true);
        }
      };
      ws.onclose = () => { if (!stopped) reconnect = setTimeout(connect, 2500); };
      ws.onerror = () => { try { ws.close(); } catch {} };
    }
    connect();
    return () => { stopped = true; if (reconnect) clearTimeout(reconnect); try { wsRef.current?.close(); } catch {} };
  }, [user?.id]);

  async function startMonitor(callId, mode) {
    try {
      await api.post("/supervisor/monitor", { call_id: callId, mode });
      toast.success(`${mode.toUpperCase()} active`);
      fetchFloor(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start monitor"); }
  }
  async function stopMonitor(monitorId) {
    try {
      await api.post(`/supervisor/monitor/${monitorId}/end`);
      toast.success("Monitor ended");
      fetchFloor(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't end monitor"); }
  }

  async function forceLogout(agent) {
    if (!window.confirm(`Force logout ${agent.name}? They will be signed out immediately and dropped from all queues.`)) return;
    try {
      await api.post(`/supervisor/agents/${agent.id}/force-logout`);
      toast.success(`${agent.name} has been signed out`);
      fetchFloor(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't force logout"); }
  }

  async function viewTranscript(agent, convId) {
    setTranscript({ agent, convId, messages: [], loading: true });
    try {
      const r = await api.get(`/conversations/${convId}`);
      setTranscript({ agent, convId, messages: r.data.conversation?.messages || [], loading: false });
    } catch (e) {
      // The conversation may not have materialised yet (agent just focused a
      // chat window). Still open the dialog in a friendly read-only state.
      setTranscript({ agent, convId, messages: [], loading: false, empty: true });
    }
  }

  // Elapsed seconds since the snapshot was fetched — keeps durations ticking
  // between 5s polls.
  const drift = Math.floor((now - fetchedAtRef.current) / 1000);

  const filteredAgents = useMemo(() => {
    if (!floor) return [];
    const term = q.trim().toLowerCase();
    return floor.agents.filter((a) => {
      if (filter !== "all" && bucketOf(a.current_activity) !== filter) return false;
      if (term && !(`${a.name} ${a.extension} ${a.email}`.toLowerCase().includes(term))) return false;
      return true;
    });
  }, [floor, filter, q]);

  if (loading) {
    return <div className="h-[calc(100vh-104px)] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!floor) return null;

  return (
    <div className="p-4 lg:p-5 space-y-4 max-w-[1920px] mx-auto" data-testid="supervisor-floor-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-display text-xl font-bold flex items-center gap-2">
          <RadioTower className="w-5 h-5 text-primary" /> Live Dashboard
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <LiveClock />
          <MonitorPhone />
          <CampaignFilter
            campaigns={floor.campaigns || []}
            selected={campaignIds}
            open={campaignOpen}
            setOpen={setCampaignOpen}
            onChange={setCampaignIds}
          />
        </div>
      </div>
      <KpiStrip kpis={floor.kpis} refreshing={refreshing} onRefresh={() => fetchFloor()} />

      {/* Live Calls in Queue */}
      <section>
        <SectionTitle icon={Headphones} label="Live Calls in Queue" count={(floor.queue_calls || []).length} />
        <QueueCallsList calls={floor.queue_calls || []} drift={drift} />
      </section>

      {/* Active monitors banner */}
      {activeMonitors.length > 0 && (
        <Card className="p-3 border-amber-300 bg-amber-50/40 dark:bg-amber-950/30" data-testid="active-monitors-banner">
          <div className="flex flex-wrap items-center gap-3">
            <RadioTower className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold">Active monitors:</span>
            {activeMonitors.map((m) => (
              <Badge key={m.id} variant="outline" className="gap-1.5 py-1 px-2" data-testid={`active-monitor-${m.id}`}>
                <span className="uppercase text-[9px] font-bold tracking-wider">{m.mode}</span>
                <span>·</span>
                <span>{m.supervisor_name} → {m.agent_name || "agent"}</span>
                <button onClick={() => stopMonitor(m.id)} className="ml-1 text-muted-foreground hover:text-foreground" data-testid={`stop-monitor-${m.id}`}>
                  <PhoneOff className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Agents */}
      <section>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <SectionTitle icon={Users2} label="Agents" count={floor.agents.length} />
          <div className="relative ml-auto w-full sm:w-56">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="floor-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search agent…" className="pl-8 h-8 text-sm" />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="floor-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`floor-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                filter === f.key ? "bg-primary text-primary-foreground border-primary"
                                 : "bg-muted/40 hover:bg-muted border-transparent"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {filteredAgents.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground text-center" data-testid="floor-empty">No agents match this filter.</Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredAgents.map((a) => (
              <AgentCard key={a.id} a={a} drift={drift} activeMonitors={activeMonitors}
                onMonitor={startMonitor} onStopMonitor={stopMonitor}
                onViewTranscript={viewTranscript} onForceLogout={forceLogout}
                isMe={a.id === user?.id} />
            ))}
          </div>
        )}
        <Legend />
      </section>

      <TranscriptDialog transcript={transcript} onClose={() => setTranscript(null)} />
    </div>
  );
}

function SectionTitle({ icon: Icon, label, count }) {
  return (
    <h2 className="font-display text-base font-bold flex items-center gap-2">
      <Icon className="w-4 h-4 text-primary" />{label}
      <Badge variant="outline" className="text-[10px]">{count}</Badge>
    </h2>
  );
}

function CampaignFilter({ campaigns, selected, open, setOpen, onChange }) {
  const label = selected.length === 0 ? "All campaigns"
    : selected.length === 1 ? (campaigns.find((c) => c.id === selected[0])?.name || "1 campaign")
    : `${selected.length} campaigns`;
  function toggle(id) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }
  return (
    <div className="relative">
      <Button data-testid="campaign-filter-trigger" variant="outline" size="sm"
        className="gap-2" onClick={() => setOpen(!open)}>
        <Target className="w-3.5 h-3.5" />
        <span className="max-w-[160px] truncate">{label}</span>
        <ChevronDown className="w-3.5 h-3.5 opacity-60" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <Card className="absolute right-0 mt-1 z-50 w-64 p-1.5 shadow-lg" data-testid="campaign-filter-menu">
            <button data-testid="campaign-opt-all"
              onClick={() => { onChange([]); }}
              className={`w-full text-left text-sm px-2 py-1.5 rounded-md hover:bg-muted ${selected.length === 0 ? "font-semibold" : ""}`}>
              All campaigns
            </button>
            <div className="h-px bg-border my-1" />
            <ScrollArea className="max-h-56">
              {campaigns.length === 0 && <div className="text-xs text-muted-foreground px-2 py-2">No campaigns</div>}
              {campaigns.map((c) => (
                <button key={c.id} data-testid={`campaign-opt-${c.id}`}
                  onClick={() => toggle(c.id)}
                  className="w-full text-left text-sm px-2 py-1.5 rounded-md hover:bg-muted flex items-center gap-2">
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center text-[9px] ${selected.includes(c.id) ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"}`}>
                    {selected.includes(c.id) ? "✓" : ""}
                  </span>
                  <span className="truncate flex-1">{c.name}</span>
                  {c.mode && <span className="text-[9px] uppercase text-muted-foreground">{c.mode}</span>}
                </button>
              ))}
            </ScrollArea>
          </Card>
        </>
      )}
    </div>
  );
}

// Premium LIGHT tile tones for the Live Dashboard — on-brand (Connect7 blue +
// red). Soft tinted surfaces, tinted icon chips and numerals; red family is
// reserved for attention/negative states, blue family for active/positive,
// slate for passive. Dark-mode variants included.
// Live Dashboard tile palette — matched to the admin Overview/Dashboard tiles:
// blue (info/active), green (positive), orange (attention/waiting), red (alert),
// slate (passive/neutral). Same accent HSLs as Overview PRIMARY tiles.
const TILE_TONES = {
  blue:   "bg-blue-50/70 dark:bg-blue-950/25 ring-1 ring-blue-100 dark:ring-blue-900/40",
  green:  "bg-emerald-50/70 dark:bg-emerald-950/25 ring-1 ring-emerald-100 dark:ring-emerald-900/40",
  orange: "bg-amber-50/70 dark:bg-amber-950/25 ring-1 ring-amber-100 dark:ring-amber-900/40",
  red:    "bg-red-50/80 dark:bg-red-950/25 ring-1 ring-red-100 dark:ring-red-900/40",
  slate:  "bg-slate-50 dark:bg-slate-900/40 ring-1 ring-slate-200/80 dark:ring-slate-800",
};
const TILE_ICON = {
  blue:   "bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300",
  green:  "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-300",
  orange: "bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-300",
  red:    "bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-300",
  slate:  "bg-slate-200/70 text-slate-500 dark:bg-slate-800 dark:text-slate-300",
};
const TILE_VAL = {
  blue:   "text-blue-700 dark:text-blue-200",
  green:  "text-emerald-700 dark:text-emerald-200",
  orange: "text-amber-700 dark:text-amber-200",
  red:    "text-red-600 dark:text-red-300",
  slate:  "text-slate-700 dark:text-slate-200",
};

const TILE_ACCENT = {
  blue:   "217 91% 55%",
  green:  "158 64% 40%",
  orange: "38 92% 50%",
  red:    "0 72% 51%",
  slate:  "215 16% 47%",
};

function PremiumTile({ icon: Icon, label, value, sub, tone = "slate", testId, pulse }) {
  const accent = TILE_ACCENT[tone] || TILE_ACCENT.slate;
  return (
    <div
      data-testid={testId}
      className={`group relative overflow-hidden rounded-xl pl-3.5 pr-3 py-2.5 border border-transparent shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${TILE_TONES[tone] || TILE_TONES.slate}`}
    >
      {/* left accent bar */}
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: `hsl(${accent})` }} />
      {/* corner glow */}
      <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl opacity-25 group-hover:opacity-45 transition-opacity"
           style={{ background: `hsl(${accent})` }} />
      <div className="relative flex items-center gap-1.5">
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-lg ${TILE_ICON[tone] || TILE_ICON.slate} ${pulse && Number(value) > 0 ? "animate-pulse" : ""}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground leading-tight">{label}</span>
      </div>
      <div className={`relative mt-1.5 font-mono text-[26px] font-bold leading-none tabular-nums ${TILE_VAL[tone] || TILE_VAL.slate}`}>{value}</div>
      {sub && <div className="relative text-[9px] text-muted-foreground mt-1 leading-tight font-medium">{sub}</div>}
    </div>
  );
}

function KpiPanel({ title, icon: Icon, accent, children, action }) {
  return (
    <Card className="p-3.5 overflow-hidden" data-testid={`kpi-panel-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-1.5 h-5 rounded-full ${accent}`} />
        <Icon className="w-4 h-4 text-muted-foreground" />
        <h3 className="font-display text-sm font-bold tracking-tight">{title}</h3>
        {action}
      </div>
      {children}
    </Card>
  );
}

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const date = now.toLocaleDateString([], {
    timeZone: DISPLAY_TZ, weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });
  const time = now.toLocaleTimeString([], {
    timeZone: DISPLAY_TZ, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  return (
    <div data-testid="live-clock"
      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60 border border-border text-sm">
      <Clock className="w-3.5 h-3.5 text-primary" />
      <span className="font-medium hidden sm:inline">{date}</span>
      <span className="font-mono tabular-nums text-foreground">{time}</span>
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{DISPLAY_TZ.split("/").pop()}</span>
    </div>
  );
}

function KpiStrip({ kpis, refreshing, onRefresh }) {
  const inboundSla = kpis.inbound_sla_pct ?? 0;
  const inboundSlaTone = inboundSla >= 90 ? "green" : inboundSla >= 70 ? "orange" : "red";
  const chans = kpis.channel_breakdown || {};

  const agentTiles = [
    { icon: LogIn,        label: "Logged in",  value: kpis.agents_logged_in,   tone: "blue",   testId: "kpi-logged-in" },
    { icon: CheckCircle2, label: "Ready",      value: kpis.agents_ready,       tone: "green",  testId: "kpi-ready" },
    { icon: UserCheck,    label: "Available",  value: kpis.agents_available,   tone: "green",  testId: "kpi-available" },
    { icon: WifiOff,      label: "Offline",    value: kpis.agents_offline,     tone: "slate",  testId: "kpi-offline" },
    { icon: PhoneCall,    label: "In calls",   value: kpis.agents_on_call,     tone: "blue",   testId: "kpi-on-call" },
    { icon: PhoneOutgoing,label: "Manual",     value: kpis.agents_manual,      tone: "blue",   testId: "kpi-manual" },
    { icon: MessageSquare,label: "On digital", value: kpis.agents_on_digital,  tone: "blue",   testId: "kpi-on-digital" },
    { icon: NotebookPen,  label: "Wrap",       value: kpis.agents_wrap,        tone: "orange", testId: "kpi-wrap" },
    { icon: Coffee,       label: "Paused",     value: kpis.agents_paused,      tone: "orange", testId: "kpi-paused" },
    { icon: Unplug,       label: "Dead calls", value: kpis.agents_in_dead_calls, tone: "red",  testId: "kpi-dead", pulse: true },
  ];

  const callTiles = [
    { icon: PhoneForwarded, label: "Dialing",      value: kpis.calls_dialing,          tone: "blue",   testId: "kpi-dialing", pulse: kpis.calls_dialing > 0, sub: "being placed" },
    { icon: PhoneIncoming, label: "Ringing",       value: kpis.calls_ringing,          tone: "orange", testId: "kpi-ringing", pulse: true, sub: "inbound / agent" },
    { icon: Target,        label: "Leads to dial", value: kpis.active_leads_dialable,  tone: "blue",   testId: "kpi-active-leads" },
    { icon: Hash,          label: "In IVR",        value: kpis.calls_in_ivr,           tone: "blue",   testId: "kpi-in-ivr" },
    { icon: Layers,        label: "In queue",      value: kpis.calls_waiting,          tone: kpis.calls_waiting > 0 ? "orange" : "slate", testId: "kpi-waiting", pulse: true },
    { icon: PhoneIncoming, label: "Inbound today",  value: kpis.calls_today_inbound,    tone: "blue",   testId: "kpi-inbound-today" },
    { icon: PhoneOutgoing, label: "Outbound today", value: kpis.calls_today_outbound,   tone: "slate",  testId: "kpi-outbound-today" },
    { icon: PhoneMissed,   label: "Dropped IN",    value: kpis.dropped_inbound,        tone: kpis.dropped_inbound > 0 ? "red" : "slate", testId: "kpi-dropped-in", sub: `${kpis.dropped_inbound_pct}%` },
    { icon: PhoneOff,      label: "Dropped OUT",   value: kpis.dropped_outbound,       tone: kpis.dropped_outbound > 0 ? "red" : "slate", testId: "kpi-dropped-out", sub: `${kpis.dropped_outbound_pct}%` },
    { icon: CheckCircle2,  label: "Answered",      value: kpis.answered_today,         tone: "green",  testId: "kpi-answered" },
    { icon: Signal,        label: "Inbound SLA",   value: `${inboundSla}%`,            tone: inboundSlaTone, testId: "kpi-inbound-sla", sub: "vs queue target" },
    { icon: PhoneForwarded, label: "Outbound Connect", value: `${kpis.outbound_connect_pct ?? 0}%`, tone: "blue", testId: "kpi-outbound-connect", sub: "answered / dialed" },
    { icon: TrendingUp,    label: "Dial level",    value: kpis.avg_dial_level,         tone: "slate",  testId: "kpi-dial-level" },
    { icon: Megaphone,     label: "VB Dialing",    value: kpis.vb_dialing,             tone: "blue",   testId: "kpi-vb-dialing", pulse: kpis.vb_dialing > 0 },
    { icon: Megaphone,     label: "VB Answered",   value: kpis.vb_answered_today,      tone: "green",  testId: "kpi-vb-answered" },
    { icon: Megaphone,     label: "VB today",      value: kpis.vb_calls_today,         tone: "slate",  testId: "kpi-vb-calls-today" },
    { icon: Megaphone,     label: "VB Connect",    value: `${kpis.vb_connect_pct ?? 0}%`, tone: "blue", testId: "kpi-vb-connect", sub: "answered / total" },
    { icon: BarChart3,     label: "Total today",   value: kpis.total_calls_all_today,  tone: "blue",   testId: "kpi-total-today", sub: "in + out + VB" },
  ];

  return (
    <div className="space-y-3" data-testid="kpi-strip">
      <KpiPanel title="Agents Status" icon={Users2} accent="bg-gradient-to-b from-blue-500 to-blue-700"
        action={
          <Button data-testid="floor-refresh" size="sm" variant="outline" onClick={onRefresh} disabled={refreshing} className="ml-auto gap-1.5 h-7">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        }>
        <div className="grid grid-cols-2 sm:grid-cols-5 2xl:grid-cols-10 gap-2.5">
          {agentTiles.map((t) => <PremiumTile key={t.testId} {...t} />)}
        </div>
      </KpiPanel>

      <KpiPanel title="Call Status" icon={PhoneCall} accent="bg-gradient-to-b from-red-500 to-rose-600">
        <div className="grid grid-cols-2 sm:grid-cols-5 2xl:grid-cols-10 gap-2.5">
          {callTiles.map((t) => <PremiumTile key={t.testId} {...t} />)}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 mt-3 border-t text-xs">
          <span className="text-muted-foreground">AHT <b className="text-foreground">{fmtAge(kpis.avg_aht_seconds)}</b> · Abandoned {kpis.abandoned_today}</span>
          {Object.keys(chans).length > 0 && (
            <span className="flex items-center gap-2">
              {Object.entries(chans).map(([ch, n]) => {
                const m = CHANNEL_META[ch] || {};
                const Icon = m.Icon || MessageSquare;
                return <span key={ch} className="inline-flex items-center gap-1 text-muted-foreground" data-testid={`kpi-chan-${ch}`}><Icon className="w-3 h-3" />{n}</span>;
              })}
            </span>
          )}
        </div>
      </KpiPanel>
    </div>
  );
}

function QueueCallsList({ calls, drift }) {
  if (!calls || calls.length === 0) {
    return (
      <Card className="p-6 text-sm text-muted-foreground text-center" data-testid="queue-calls-empty">
        No calls waiting in queue right now.
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden" data-testid="queue-calls-list">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="text-left font-semibold px-3 py-2">Queue / Campaign</th>
              <th className="text-left font-semibold px-3 py-2">Caller Number</th>
              <th className="text-left font-semibold px-3 py-2">Type</th>
              <th className="text-right font-semibold px-3 py-2">Queue Time</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((c) => {
              const wait = (c.wait_seconds || 0) + (drift || 0);
              const hot = wait >= 30;
              return (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors" data-testid={`queue-call-${c.id}`}>
                  <td className="px-3 py-2 font-medium truncate max-w-[240px]" data-testid={`queue-call-label-${c.id}`}>
                    <span className="inline-flex items-center gap-1.5">
                      {c.is_inbound ? <PhoneIncoming className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                    : <PhoneOutgoing className="w-3.5 h-3.5 text-sky-600 shrink-0" />}
                      <span className="truncate">{c.label}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-muted-foreground" data-testid={`queue-call-num-${c.id}`}>{c.caller_num || "Unknown"}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`text-[10px] ${queueTypeChip(c.type, c.is_inbound)}`} data-testid={`queue-call-type-${c.id}`}>
                      {c.type}
                    </Badge>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${hot ? "text-red-600 font-bold" : "text-foreground"}`} data-testid={`queue-call-wait-${c.id}`}>
                    {fmtAge(wait)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function queueTypeChip(type, isInbound) {
  if (isInbound) return "text-emerald-700 border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-300";
  return "text-sky-700 border-sky-400 bg-sky-50 dark:bg-sky-950/30 dark:text-sky-300";
}

function AgentCard({ a, drift, activeMonitors, onMonitor, onStopMonitor, onViewTranscript, onForceLogout, isMe }) {
  const ca = a.current_activity || { kind: "idle", status: "offline", label: "Offline", duration_seconds: 0 };
  const vis = activityVisual(ca);
  const Icon = vis.Icon;
  const isVoiceLive = ca.kind === "voice" && (ca.status === "talking" || ca.status === "dead_call");
  const isDigital = ca.kind === "digital";
  const myMonitor = activeMonitors.find((m) => m.call_id === ca.call_id);
  // Live duration = snapshot duration + drift since fetch (only tick when "live").
  const ticking = ca.kind !== "idle" || ca.status === "ready" || ca.status === "available" || ca.status === "paused" || ca.status === "wrap" || ca.status === "break" || ca.status === "lunch";
  const dur = (ca.duration_seconds || 0) + (ticking ? drift : 0);
  // Force logout is allowed when the agent is logged in but NOT on an active
  // call. A stuck dead call becomes force-logoutable after 2 minutes.
  const ACTIVE_CALL = ["talking", "held", "originating", "ringing"];
  const isOffline = ca.status === "offline";
  const onActiveCall = ACTIVE_CALL.includes(ca.status) || (ca.status === "dead_call" && dur < 120);
  const canForceLogout = !isMe && !isOffline && !onActiveCall;

  return (
    <Card className={`p-3 ${vis.ring || ""}`} data-testid={`agent-card-${a.id}`}>
      <div className="flex items-start gap-2.5">
        <div className="relative">
          <Avatar className="h-10 w-10"><AvatarFallback style={{ background: a.avatar_color }} className="text-white text-xs font-bold">{a.initials}</AvatarFallback></Avatar>
          <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card ${vis.dot}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-display font-bold text-sm truncate" data-testid={`agent-name-${a.id}`}>{a.name}</span>
            {isMe && <Badge variant="outline" className="text-[9px] h-4 px-1">you</Badge>}
          </div>
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <span className="font-mono">ext {a.extension || "—"}</span>
            <span>·</span>
            <span className="capitalize">{(a.role || "").replace("_", " ")}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Calls</div>
          <div className="font-display font-bold text-sm">{a.calls_today}</div>
        </div>
      </div>

      {/* Status row */}
      <div className="mt-2.5 flex items-center justify-between gap-2">
        <Badge variant="outline" className={`gap-1.5 py-1 border ${vis.chip}`} data-testid={`agent-state-${a.id}`}>
          <Icon className="w-3 h-3" />
          {ca.label}
        </Badge>
        <span className="text-[11px] font-mono text-muted-foreground" data-testid={`agent-duration-${a.id}`}>{fmtAge(dur)}</span>
      </div>

      {/* Reason (idle only) */}
      {ca.kind === "idle" && a.presence_reason && (
        <div className="mt-1 text-[10px] text-muted-foreground italic truncate" title={a.presence_reason} data-testid={`agent-reason-${a.id}`}>
          {a.presence_reason}
        </div>
      )}

      {/* Activity detail panel */}
      {(isVoiceLive || isDigital || (ca.kind === "voice")) && (
        <div className="mt-2.5 p-2 rounded-md bg-muted/40 border border-border" data-testid={`agent-activity-${a.id}`}>
          <div className="flex items-center gap-1.5 text-xs">
            <Icon className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="font-semibold truncate">{ca.peer_name || ca.peer || (isDigital ? "Customer chat" : "Customer")}</span>
            {ca.kind === "voice" && ca.direction === "inbound"
              ? <Phone className="w-3 h-3 text-emerald-600 ml-auto" />
              : ca.kind === "voice" ? <PhoneCall className="w-3 h-3 text-sky-600 ml-auto" /> : null}
          </div>
          {ca.peer && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{ca.peer}</div>}

          {/* Voice monitor controls */}
          {isVoiceLive && !isMe && (
            <div className="grid grid-cols-3 gap-1.5 mt-2">
              {myMonitor ? (
                <Button data-testid={`monitor-stop-${a.id}`} onClick={() => onStopMonitor(myMonitor.id)} variant="destructive" size="sm" className="col-span-3 h-7 gap-1 text-xs">
                  <PhoneOff className="w-3 h-3" />End {myMonitor.mode.toUpperCase()}
                </Button>
              ) : (
                <>
                  <Button data-testid={`monitor-spy-${a.id}`} onClick={() => onMonitor(ca.call_id, "spy")} variant="outline" size="sm" className="h-7 gap-1 text-xs px-1">
                    <Eye className="w-3 h-3" />Spy
                  </Button>
                  <Button data-testid={`monitor-whisper-${a.id}`} onClick={() => onMonitor(ca.call_id, "whisper")} variant="outline" size="sm" className="h-7 gap-1 text-xs px-1">
                    <MessageSquare className="w-3 h-3" />Whisper
                  </Button>
                  <Button data-testid={`monitor-barge-${a.id}`} onClick={() => onMonitor(ca.call_id, "barge")} variant="outline" size="sm" className="h-7 gap-1 text-xs px-1 border-amber-400 text-amber-700">
                    <Volume2 className="w-3 h-3" />Barge
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Digital read-only transcript view */}
          {isDigital && ca.conv_id && (
            <Button data-testid={`view-transcript-${a.id}`} onClick={() => onViewTranscript(a, ca.conv_id)} variant="outline" size="sm" className="w-full h-7 gap-1 text-xs mt-2">
              <Eye className="w-3 h-3" />View live transcript
            </Button>
          )}
        </div>
      )}

      {/* Force logout — disabled while the agent is on an active call */}
      {!isMe && !isOffline && (
        <Button
          data-testid={`force-logout-${a.id}`}
          onClick={() => onForceLogout(a)}
          disabled={!canForceLogout}
          variant="outline" size="sm"
          className="w-full h-7 mt-2 gap-1 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 disabled:opacity-40 disabled:cursor-not-allowed"
          title={canForceLogout ? "Force sign this agent out" : "Can't force logout while the agent is on an active call"}
        >
          <LogOut className="w-3 h-3" /> Force logout
        </Button>
      )}
    </Card>
  );
}

function TranscriptDialog({ transcript, onClose }) {
  return (
    <Dialog open={!!transcript} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="transcript-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Live transcript — {transcript?.agent?.name}
            <Badge variant="outline" className="text-[10px]">read-only</Badge>
          </DialogTitle>
        </DialogHeader>
        {transcript?.loading ? (
          <div className="py-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <ScrollArea className="h-[60vh] pr-3">
            <div className="space-y-2">
              {(transcript?.messages || []).length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8">
                  {transcript?.empty
                    ? "This conversation hasn't started yet — the agent has it open but no messages have been exchanged."
                    : "No messages yet."}
                </div>
              )}
              {(transcript?.messages || []).map((m, i) => {
                const fromCustomer = m.from === "customer";
                return (
                  <div key={m.id || i} className={`flex ${fromCustomer ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[78%] rounded-lg px-3 py-2 text-sm ${fromCustomer ? "bg-muted" : "bg-primary text-primary-foreground"}`}>
                      <div className="whitespace-pre-wrap break-words">{m.body}</div>
                      <div className={`text-[9px] mt-1 ${fromCustomer ? "text-muted-foreground" : "text-primary-foreground/70"}`}>{m.ts ? new Date(m.ts).toLocaleTimeString([], { timeZone: DISPLAY_TZ }) : ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Legend() {
  const items = [
    { c: "bg-emerald-500", l: "Available / Ready" },
    { c: "bg-sky-500", l: "In call < 1m" },
    { c: "bg-blue-600", l: "In call 1–5m" },
    { c: "bg-indigo-700", l: "In call > 5m" },
    { c: "bg-amber-500", l: "Ringing" },
    { c: "bg-cyan-500", l: "Manual dial" },
    { c: "bg-rose-500", l: "Paused / Break" },
    { c: "bg-orange-500", l: "Wrap-up" },
    { c: "bg-violet-500", l: "Digital channel" },
    { c: "bg-zinc-900", l: "Dead call" },
    { c: "bg-slate-400", l: "Offline" },
  ];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[10px] text-muted-foreground" data-testid="floor-legend">
      {items.map((it) => (
        <span key={it.l} className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${it.c}`} />{it.l}</span>
      ))}
    </div>
  );
}
